/**
 * Calculates time until the next interval that is a multiple of the given value
 * @param intervalMs - interval in milliseconds (can be any positive number)
 * @param fromDate - reference date (defaults to current time)
 * @returns object with information about the next interval
 *
 * @example
 * // Example 1: 15-minute intervals (900000 ms)
 * // Current time: 10:37
 * const result1 = getTimeToNextInterval(900000);
 * // Returns: {
 * //   nextIntervalDate: 10:45,
 * //   millisecondsToNext: 480000,
 * //   secondsToNext: 480,
 * //   minutesToNext: 8
 * // }
 *
 * @example
 * // Example 2: 90-minute intervals (5400000 ms)
 * // Current time: 10:37
 * const result2 = getTimeToNextInterval(5400000);
 * // Returns: {
 * //   nextIntervalDate: 10:30 next day (if started from 00:00, 01:30, 03:00, 04:30, 06:00, 07:30, 09:00, 10:30, 12:00...)
 * //   millisecondsToNext: varies,
 * //   secondsToNext: varies,
 * //   minutesToNext: varies
 * // }
 *
 * @example
 * // Example 3: 5-minute intervals (300000 ms)
 * // Current time: 14:23
 * const result3 = getTimeToNextInterval(300000);
 * // Returns: {
 * //   nextIntervalDate: 14:25,
 * //   millisecondsToNext: 120000,
 * //   secondsToNext: 120,
 * //   minutesToNext: 2
 * // }
 */
export function getTimeToNextInterval(intervalMs: number, fromDate: Date = new Date()) {
  if (intervalMs <= 0) {
    throw new Error('intervalMs must be greater than 0');
  }

  const currentDate = new Date(fromDate);
  const currentTime = currentDate.getTime();

  // Get start of the day in milliseconds
  const startOfDay = new Date(currentDate);
  startOfDay.setHours(0, 0, 0, 0);
  const startOfDayTime = startOfDay.getTime();

  // Calculate milliseconds from the start of the day
  const msFromDayStart = currentTime - startOfDayTime;

  // Find next interval that is a multiple of intervalMs
  let nextIntervalMsFromDayStart = Math.ceil(msFromDayStart / intervalMs) * intervalMs;

  // If we're exactly on the interval, we need the next interval
  if (msFromDayStart === nextIntervalMsFromDayStart) {
    nextIntervalMsFromDayStart += intervalMs;
  }

  // Create date for the next interval
  const nextIntervalDate = new Date(startOfDayTime + nextIntervalMsFromDayStart);

  // Calculate difference
  const millisecondsToNext = nextIntervalDate.getTime() - currentTime;
  const secondsToNext = Math.floor(millisecondsToNext / 1000);
  const minutesToNext = Math.floor(secondsToNext / 60);

  return {
    currentIntervalDate: currentDate,
    millisecondsToNext,
    minutesToNext,
    nextIntervalDate,
    secondsToNext,
  };
}
